import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { PermissionGate } from '@/components/PermissionGate';
import { Permission } from '@/common/enums/permissions.enum';
import {
  pipelineService,
  CANAIS,
  ETAPAS,
  canalMeta,
  etapaMeta,
  formatCurrency,
  type PipelineCanal,
  type LeadEtapa,
} from '@/features/pipeline/services/pipeline.service';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Search } from 'lucide-react';
import { PageHeader } from '@/components/ui/page-header';

export const Route = createFileRoute('/_authenticated/pipeline/leads/')({
  component: LeadsList,
});

function LeadsList() {
  return (
    <PermissionGate permission={Permission.PIPELINE_VIEW}>
      <LeadsListContent />
    </PermissionGate>
  );
}

function LeadsListContent() {
  const { data = [] } = useQuery({ queryKey: ['pipeline-leads'], queryFn: pipelineService.listLeads });
  const [q, setQ] = useState('');
  const [canal, setCanal] = useState<string>('todos');
  const [etapa, setEtapa] = useState<string>('todos');

  const filtered = useMemo(() => {
    return data.filter((l) => {
      if (canal !== 'todos' && l.canalOrigem !== canal) return false;
      if (etapa !== 'todos' && l.etapa !== etapa) return false;
      if (q) {
        const t = q.toLowerCase();
        if (!l.nome.toLowerCase().includes(t) && !(l.empresa ?? '').toLowerCase().includes(t)) return false;
      }
      return true;
    });
  }, [data, q, canal, etapa]);

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Leads"
        description={`${filtered.length} de ${data.length}`}
        actions={
          <Link
            to="/pipeline/leads/novo"
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" /> Novo lead
          </Link>
        }
      />

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nome ou empresa..."
            className="pl-8"
          />
        </div>
        <Select value={canal} onValueChange={setCanal}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Canal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os canais</SelectItem>
            {CANAIS.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={etapa} onValueChange={setEtapa}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Etapa" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todas as etapas</SelectItem>
            {ETAPAS.map((e) => (
              <SelectItem key={e.value} value={e.value}>
                {e.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Lead</TableHead>
              <TableHead>Canal</TableHead>
              <TableHead>Etapa</TableHead>
              <TableHead className="text-right">Valor</TableHead>
              <TableHead>Contato</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map((l) => {
              const cm = canalMeta(l.canalOrigem as PipelineCanal);
              const em = etapaMeta(l.etapa as LeadEtapa);
              const Ic = cm.icon;
              return (
                <TableRow key={l.id} className="cursor-pointer">
                  <TableCell>
                    <Link to="/pipeline/leads/$id" params={{ id: l.id }} className="block">
                      <div className="font-medium">{l.nome}</div>
                      {l.empresa && <div className="text-xs text-muted-foreground">{l.empresa}</div>}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 text-sm">
                      <Ic className="h-3.5 w-3.5" />
                      {cm.label}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge style={{ background: em.color, color: 'white' }}>{em.label}</Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(Number(l.valorFechado ?? l.valorProposta ?? 0))}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{l.email || l.telefone || '—'}</TableCell>
                </TableRow>
              );
            })}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-10 text-muted-foreground">
                  Nenhum lead encontrado.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
